import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Alert, Modal } from '@components/bootstrap';
import { usersService } from '../../../services/usersService';
import { useAuth } from '../../../hooks/useAuth';
import './UserManagement.scss';
const UserManagement = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedUser, setSelectedUser] = useState(null);
    const [modalData, setModalData] = useState({
        name: '',
        email: '',
        role: 'user'
    });
    const roles = [
        { value: 'admin', label: 'Administrador' },
        { value: 'moderator', label: 'Moderador' },
        { value: 'user', label: 'Usuário' }
    ];
    useEffect(() => {
        if (currentUser) {
            fetchUsers();
        }
    }, [currentUser]);
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            let usersData;
            if (currentUser?.role === 'superadmin') {
                console.log('O BO TA AQUI 2');
                usersData = await usersService.getAllUsers();
            }
            else if (currentUser?.accountId) {
                usersData = await usersService.getUsersByAccount(currentUser.accountId);
            }
            else {
                console.warn('User without accountId, fetching all users as fallback');
                usersData = await usersService.getAllUsers();
            }
            // Garantir que usersData é um array
            if (!Array.isArray(usersData)) {
                console.error('❌ usersData is not an array:', usersData);
                setError('Erro: dados de usuários inválidos');
                setUsers([]);
                return;
            }
            // Filtrar usuários válidos e normalizar estrutura
            const validUsers = usersData
                .filter(user => user &&
                typeof user === 'object' &&
                user.id &&
                user.name &&
                user.email)
                .map(user => ({
                id: user.id,
                name: user.name,
                email: user.email,
                role: user.role || 'user',
                avatar: user.avatar || `https://picsum.photos/200/200?random=${Math.random()}`,
                isActive: user.isActive !== undefined ? user.isActive : true,
                createdAt: user.createdAt || new Date().toISOString(),
                updatedAt: user.updatedAt || user.createdAt || new Date().toISOString(),
                accountId: user.accountId || currentUser?.accountId || '',
            }));
            console.log(`✅ Loaded ${validUsers.length} users`);
            setUsers(validUsers);
        }
        catch (err) {
            console.error('❌ Error fetching users:', err);
            // Tratar erros específicos
            if (err.message?.includes('not found')) {
                setError('Nenhum usuário encontrado para esta conta.');
            }
            else if (err.message?.includes('network') || err.message?.includes('fetch')) {
                setError('Erro de conexão. Verifique sua internet e tente novamente.');
            }
            else {
                setError('Erro ao carregar usuários. Tente novamente em alguns instantes.');
            }
            setUsers([]);
        }
        finally {
            setLoading(false);
        }
    };
    const handleAddUser = () => {
        setModalMode('add');
        setModalData({ name: '', email: '', role: 'user', password: '' });
        setSelectedUser(null);
        setShowModal(true);
    };
    const handleEditUser = (user) => {
        setModalMode('edit');
        setModalData({
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role
        });
        setSelectedUser(user);
        setShowModal(true);
    };
    const handleDeleteUser = (user) => {
        setModalMode('delete');
        setSelectedUser(user);
        setShowModal(true);
    };
    const handleSaveUser = async (data) => {
        try {
            setLoading(true);
            setError(null);
            if (modalMode === 'add') {
                const createUserData = {
                    name: data.name,
                    email: data.email,
                    password: data.password || '',
                    role: data.role,
                    accountId: currentUser?.accountId || '',
                    avatar: `https://picsum.photos/200/200?random=${Date.now()}`
                };
                await usersService.createUser(createUserData);
            }
            else if (modalMode === 'edit' && selectedUser) {
                const updateUserData = {
                    name: data.name,
                    email: data.email,
                    role: data.role
                };
                await usersService.updateUser(selectedUser.id, updateUserData);
            }
            setShowModal(false);
            fetchUsers(); // Recarregar lista
        }
        catch (err) {
            if (err.response?.status === 409) {
                const errorMessage = err.response?.data?.message || 'Email já existe nesta conta';
                setError(errorMessage);
            }
            else {
                setError('Erro ao salvar usuário');
            }
            console.error('Error saving user:', err);
        }
        finally {
            setLoading(false);
        }
    };
    const handleConfirmDelete = async () => {
        if (!selectedUser)
            return;
        try {
            setLoading(true);
            setError(null);
            await usersService.deleteUser(selectedUser.id);
            setShowModal(false);
            fetchUsers(); // Recarregar lista
        }
        catch (err) {
            setError('Erro ao excluir usuário');
            console.error('Error deleting user:', err);
        }
        finally {
            setLoading(false);
        }
    };
    const handleToggleUserStatus = async (user) => {
        try {
            setLoading(true);
            setError(null);
            if (user.isActive) {
                await usersService.deactivateUser(user.id);
            }
            else {
                await usersService.activateUser(user.id);
            }
            fetchUsers(); // Recarregar lista
        }
        catch (err) {
            setError('Erro ao alterar status do usuário');
            console.error('Error toggling user status:', err);
        }
        finally {
            setLoading(false);
        }
    };
    const getRoleLabel = (role) => {
        const roleObj = roles.find(r => r.value === role);
        return roleObj ? roleObj.label : role;
    };
    const getRoleBadgeVariant = (role) => {
        switch (role) {
            case 'admin':
                return 'danger';
            case 'moderator':
                return 'warning';
            case 'user':
                return 'secondary';
            default:
                return 'secondary';
        }
    };
    // Verificar permissões
    const canManageUsers = currentUser?.role === 'superadmin' || currentUser?.role === 'admin';
    const canDeleteUsers = currentUser?.role === 'superadmin' ||
        (currentUser?.role === 'admin' && selectedUser?.role !== 'admin');
    if (!canManageUsers) {
        return (_jsx("div", { className: "user-management", children: _jsxs(Alert, { variant: "warning", children: [_jsx("h4", { children: "\u274C Acesso Negado" }), _jsx("p", { children: "Voc\u00EA n\u00E3o tem permiss\u00E3o para gerenciar usu\u00E1rios. Apenas administradores podem acessar esta funcionalidade." })] }) }));
    }
    return (_jsxs("div", { className: "user-management", children: [_jsx(Row, { children: _jsx(Col, { xs: 12, children: _jsxs(Card, { children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsx("h5", { className: "mb-0", children: "\uD83D\uDC65 Gest\u00E3o de Usu\u00E1rios" }), _jsx(Button, { variant: "primary", size: "sm", onClick: handleAddUser, disabled: loading, children: "\u2795 Adicionar Usu\u00E1rio" })] }), _jsxs("div", { className: "card-body", children: [error && (_jsx(Alert, { variant: "danger", onClose: () => setError(null), dismissible: true, children: error })), loading ? (_jsxs("div", { className: "text-center py-4", children: [_jsx("div", { className: "spinner-border text-primary", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Carregando..." }) }), _jsx("p", { className: "mt-2", children: "Carregando usu\u00E1rios..." })] })) : users.length === 0 ? (_jsx(Alert, { variant: "info", className: "text-center", children: "Nenhum usu\u00E1rio encontrado." })) : (_jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-hover", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Usu\u00E1rio" }), _jsx("th", { children: "Email" }), _jsx("th", { children: "Perfil" }), _jsx("th", { children: "Status" }), _jsx("th", { children: "Data de Cria\u00E7\u00E3o" }), _jsx("th", { children: "A\u00E7\u00F5es" })] }) }), _jsx("tbody", { children: users.map((user) => (_jsxs("tr", { children: [_jsx("td", { children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsx("img", { src: user.avatar || `https://picsum.photos/200/200?random=${user.id}`, alt: user.name, className: "rounded-circle me-2", style: { width: '32px', height: '32px' } }), _jsx("span", { children: user.name })] }) }), _jsx("td", { children: user.email }), _jsx("td", { children: _jsx(Badge, { variant: getRoleBadgeVariant(user.role), children: getRoleLabel(user.role) }) }), _jsx("td", { children: _jsx(Badge, { variant: user.isActive ? 'success' : 'danger', children: user.isActive ? 'Ativo' : 'Inativo' }) }), _jsx("td", { children: new Date(user.createdAt).toLocaleDateString('pt-BR') }), _jsx("td", { children: _jsxs("div", { className: "btn-group", role: "group", children: [_jsx(Button, { variant: "primary", outline: true, size: "sm", onClick: () => handleEditUser(user), disabled: loading, children: _jsx("i", { className: "fas fa-edit" }) }), _jsx(Button, { variant: user.isActive ? 'warning' : 'success', outline: true, size: "sm", onClick: () => handleToggleUserStatus(user), disabled: loading, children: _jsx("i", { className: `fas fa-${user.isActive ? 'pause' : 'play'}` }) }), canDeleteUsers && (_jsx(Button, { variant: "danger", outline: true, size: "sm", onClick: () => handleDeleteUser(user), disabled: loading, children: _jsx("i", { className: "fas fa-trash" }) }))] }) })] }, user.id))) })] }) }))] })] }) }) }), _jsx(Modal, { show: showModal, onHide: () => setShowModal(false), title: modalMode === 'add' ? 'Adicionar Usuário' :
                    modalMode === 'edit' ? 'Editar Usuário' :
                        'Excluir Usuário', size: "md", centered: true, footer: _jsxs("div", { className: "d-flex gap-2", children: [_jsx(Button, { variant: "secondary", onClick: () => setShowModal(false), children: "Cancelar" }), _jsx(Button, { variant: modalMode === 'delete' ? 'danger' : 'primary', onClick: modalMode === 'delete' ? handleConfirmDelete : () => {
                                // Trigger form submission
                                const form = document.querySelector('form');
                                if (form)
                                    form.requestSubmit();
                            }, disabled: loading, children: loading ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), modalMode === 'delete' ? 'Excluindo...' : 'Salvando...'] })) : (modalMode === 'delete' ? 'Excluir' : modalMode === 'add' ? 'Adicionar' : 'Salvar') })] }), children: modalMode === 'delete' ? (_jsxs("div", { className: "text-center", children: [_jsx("div", { className: "mb-4", children: _jsx("i", { className: "fas fa-exclamation-triangle text-warning", style: { fontSize: '3rem' } }) }), _jsx("h5", { children: "Tem certeza que deseja excluir este usu\u00E1rio?" }), _jsxs("p", { className: "text-muted", children: ["Esta a\u00E7\u00E3o n\u00E3o pode ser desfeita. O usu\u00E1rio ", _jsx("strong", { children: selectedUser?.name }), " ser\u00E1 removido permanentemente."] })] })) : (_jsxs("form", { onSubmit: (e) => {
                        e.preventDefault();
                        handleSaveUser(modalData);
                    }, children: [_jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Nome" }), _jsx("input", { type: "text", className: "form-control", value: modalData.name, onChange: (e) => setModalData({ ...modalData, name: e.target.value }), placeholder: "Digite o nome completo", required: true })] }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Email" }), _jsx("input", { type: "email", className: "form-control", value: modalData.email, onChange: (e) => setModalData({ ...modalData, email: e.target.value }), placeholder: "Digite o email", required: true })] }), modalMode === 'add' && (_jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Senha" }), _jsx("input", { type: "password", className: "form-control", value: modalData.password || '', onChange: (e) => setModalData({ ...modalData, password: e.target.value }), placeholder: "Digite a senha", required: true })] })), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Perfil" }), _jsxs("select", { className: "form-select", value: modalData.role, onChange: (e) => setModalData({ ...modalData, role: e.target.value }), required: true, children: [_jsx("option", { value: "", children: "Selecione um perfil" }), roles.map((role) => (_jsx("option", { value: role.value, children: role.label }, role.value)))] })] })] })) })] }));
};
export default UserManagement;
